// FlowBoard: an empty workspace's board, the range's AI work as one flow of
// cards. Each column is a step a session takes, from who ran it to where
// review left it; each card is a value of that step, as tall as its
// sessions, holding what the old inventories said of it (how much, how it
// went over the range, what was flagged, what it is made of: an agent's
// models, a person's machines, a server's tools). Strands between the cards
// carry the sessions, grey where nothing was flagged, amber where something
// was, red where a standard was met, so where what needs a person comes
// from reads across the board. Cards and strands are one shape, one quiet
// grey on the workspace's ground with what each card says inside it; a
// pointed card's flow is a shade up. Each agent keeps one colour, the one it
// has in the page's Agents section. Pointing at a card follows its sessions
// through every column; a click drills down to the card: the board's search
// narrows to it, and the whole page (and the dock's list of what the click
// reached) shows only what it holds. The card the search is narrowed to,
// clicked, widens it back out.
import { memo, useCallback, useMemo, useRef, useState } from "react";
import { useLendFinder } from "./BoardScope.jsx";
import { holdsClause, useWorkspaceSearch } from "../lib/workspaceSearch.js";
import { reviewClause } from "../lib/reviewClause.js";
import { AGENT_HUES, whenText } from "./WorkspaceAddOverlay.jsx";
import { SEVERITY_COLOR } from "../lib/traceReview.js";
import { scopeStepLabel } from "../lib/dimensionDefs.js";
import { NOT_IN_DIRECTORY, NO_DEPARTMENT, quoteValueIfNeeded } from "../lib/queryLang.js";
import { PROVIDER_TAG_TO_IDENTITY } from "../lib/agentIdentity.js";
import { EYEBROW_CLASS } from "../lib/ui.js";

const MET = SEVERITY_COLOR.danger;
const FLAGGED = SEVERITY_COLOR.warn;
const NUM = new Intl.NumberFormat();
const pctOf = (n, of) => (of > 0 ? `${n > 0 && n / of < 0.01 ? "<1" : Math.round((n / of) * 100)}%` : "");

export const OUTCOMES = [
  ["met", "Standard met"],
  ["not_verified", "Not checked yet"],
  ["not_met", "No standard met"],
  ["set_aside", "Set aside"],
  ["none", "Nothing flagged"],
];
export const OUTCOME_NAME = Object.fromEntries(OUTCOMES);
export const OUTCOME_WHY = {
  met: "A standard says a person should look.",
  not_verified: "Flagged; Triage has not checked them.",
  not_met: "True, but no standard asks for a person.",
  set_aside: "False, or could not be checked.",
  none: "The review found nothing worth a look.",
};
export const CLASS_OF = { met: "met", not_verified: "flagged", not_met: "flagged", set_aside: "flagged", none: "clear" };
// When a session was last at work, as the line under the board says it.
export const activeAt = (s) => (Number.isFinite(s.lastMs) ? s.lastMs : s.startMs);

// The board's fills, solid, with no lines: a card or a strand is one colour
// at most, a grey (dark at rest, brighter in a pointed card's flow, brighter
// still for the card pointed at) or the amber or red of what review flagged
// in it.
export const REST_FILL = "color-mix(in srgb, var(--muted) 8%, var(--workspace-bg))";
export const LIT_FILL = "color-mix(in srgb, var(--muted) 20%, var(--workspace-bg))";
export const HOT_FILL = "color-mix(in srgb, var(--muted) 27%, var(--workspace-bg))";
// An outcome that needs a look is its colour throughout: tinted (a shade up
// in a pointed card's flow) and charted in it, as the strands that end in it
// are.
const POOL = {
  met: { fill: `color-mix(in srgb, ${MET} 16%, var(--workspace-bg))`, hot: `color-mix(in srgb, ${MET} 24%, var(--workspace-bg))`, line: MET },
  flagged: { fill: `color-mix(in srgb, ${FLAGGED} 10%, var(--workspace-bg))`, hot: `color-mix(in srgb, ${FLAGGED} 16%, var(--workspace-bg))`, line: FLAGGED },
};
const POOL_OF = { met: "met", not_verified: "flagged", not_met: "flagged", set_aside: "flagged" };
const poolOf = (col, key) => (col.def.fixed ? POOL_OF[key] || null : null);

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-/i;
export const agentName = (tag) => {
  const cached = scopeStepLabel({ dim: "agents", ids: [tag] });
  return cached && cached !== tag ? cached : PROVIDER_TAG_TO_IDENTITY[String(tag).toLowerCase()]?.name || tag;
};
export const machineName = (v) => (UUID.test(String(v)) ? scopeStepLabel({ dim: "endpoints", ids: [v] }) : v);
export const tally = (list, of) => {
  const m = new Map();
  for (const x of list) {
    const vs = of(x);
    for (const v of Array.isArray(vs) ? vs : [vs]) if (v) m.set(v, (m.get(v) || 0) + 1);
  }
  return [...m.entries()].sort((a, b) => b[1] - a[1]);
};

// Each step a session takes: what it was at that step, what the value is
// called, the search clause that narrows to it, and what a card of it can
// hold inside: its lists, in the order they are wanted, each of how many of
// its sessions had each part (an agent's models, a person's machines).
const ON = { label: "On", of: (s) => s.machine, name: machineName };
const WORK = { label: "Work", of: (s) => s.topic, name: (v) => v };
const WITH = { label: "With", of: (s) => s.agent, name: agentName };
const BY = { label: "By", of: (s) => s.person, name: (v) => v };
const MODELS = { label: "Models", of: (s) => s.model, name: (v) => v };
// A session's group is its person's department in the directory; else the
// directory gives that person none, or has no one for the machine, each a
// group the search narrows to as any other.
export const groupOf = (s) => s.group || (s.assigned ? NO_DEPARTMENT : NOT_IN_DIRECTORY);
export const STEPS = {
  group: {
    label: "Groups", noun: "groups", of: groupOf, name: (v) => v, clause: (v) => `group:${quoteValueIfNeeded(v)}`,
    lists: [BY, WORK, WITH, MODELS],
  },
  person: {
    label: "People", noun: "people", of: (s) => s.person, name: (v) => v, clause: (v) => `person:${quoteValueIfNeeded(v)}`,
    lists: [WORK, ON, WITH, MODELS],
  },
  machine: {
    label: "Machines", noun: "machines", of: (s) => s.machine || s.machineId, name: machineName, clause: (v) => `machine:${quoteValueIfNeeded(machineName(v))}`,
    lists: [WITH, WORK, BY, MODELS],
  },
  agent: {
    label: "Agents", noun: "agents", of: (s) => s.agent, name: agentName, clause: (v) => `agent:${quoteValueIfNeeded(agentName(v))}`,
    lists: [MODELS, WORK, BY, ON],
  },
  topic: {
    label: "Work", noun: "kinds of work", of: (s) => s.topic, name: (v) => v, clause: (v) => `topic:${quoteValueIfNeeded(v)}`,
    lists: [
      { label: "Skills", of: (s) => s.skills, name: (v) => v },
      { label: "Tools", of: (s) => (s.tools || []).filter((t) => !t.startsWith("mcp__")), name: (v) => v },
      BY,
    ],
  },
  reach: {
    label: "Reached for", noun: "MCP servers and skills",
    of: (s) => (s.mcp?.[0] ? `mcp:${s.mcp[0]}` : s.skills?.[0] ? `skill:${s.skills[0]}` : "builtin"),
    name: (v) => (v === "builtin" ? "Built-in tools only" : v.startsWith("skill:") ? `${v.slice(6)} (skill)` : v.slice(4)),
    clause: (v) => (v === "builtin" ? null : `${v.startsWith("skill:") ? "skill" : "mcp"}:${quoteValueIfNeeded(v.slice(v.indexOf(":") + 1))}`),
    lists: [
      { label: "Tools", of: (s) => (s.tools || []).filter((t) => t.startsWith("mcp__")).map((t) => t.split("__").slice(2).join("__") || t), name: (v) => v },
      { label: "For", of: (s) => s.topic, name: (v) => v },
    ],
  },
  review: {
    label: "Review", noun: "outcomes", of: (s) => s.outcome || "none", name: (v) => OUTCOME_NAME[v] || v, clause: reviewClause, fixed: OUTCOMES.map(([id]) => id),
    lists: [{ ...WORK, label: "From" }, BY, WITH, MODELS],
  },
};

// The board's geometry: columns of cards and the strands between them.
const CARD_GAP = 8;
const CARD_MIN = 40;
// A review card that nothing was flagged in, beside flagged ones: its name
// and its share.
export const QUIET_H = 48;
export function layout(sessions, steps, { width, height }) {
  const total = sessions.length;
  const n = steps.length;
  if (!total || !n || width <= 0) return null;
  const gap = n > 1 ? Math.max(56, Math.min(150, width * 0.09)) : 0;
  const cw = Math.max(140, (width - gap * (n - 1)) / n);
  const keyAt = [];
  const columns = steps.map((def, ci) => {
    const counts = new Map();
    for (const s of sessions) {
      const v = def.of(s);
      if (v != null) counts.set(v, (counts.get(v) || 0) + 1);
    }
    const ranked = def.fixed ? def.fixed.filter((v) => counts.get(v)) : [...counts.keys()].sort((a, b) => counts.get(b) - counts.get(a));
    // As many cards as the column holds at a readable height; the rest are
    // one card that names them.
    const room = Math.max(2, Math.floor((height + CARD_GAP) / (CARD_MIN + 18 + CARD_GAP)));
    const kept = def.fixed || ranked.length <= room ? ranked : ranked.slice(0, room - 1);
    const folded = ranked.slice(kept.length);
    const cards = kept.map((v) => ({ key: v, count: counts.get(v) }));
    if (folded.length) cards.push({ key: "__more", count: folded.reduce((a, v) => a + counts.get(v), 0), folded: folded.map((v) => [v, counts.get(v)]) });
    const byKey = new Map(cards.map((c) => [c.key, c]));
    const foldedSet = new Set(folded);
    keyAt[ci] = (s) => {
      const v = def.of(s);
      return v == null ? null : foldedSet.has(v) ? "__more" : v;
    };
    for (const c of cards) {
      c.cls = { met: 0, flagged: 0, clear: 0 };
      c.sessions = [];
    }
    for (const s of sessions) {
      const k = keyAt[ci](s);
      if (k == null) continue;
      const c = byKey.get(k);
      c.cls[CLASS_OF[s.outcome || "none"]] += 1;
      c.sessions.push(s);
    }
    // On whole pixels, so a card's edge and the strands' ends meet exactly.
    return { ci, def, cards, byKey, x: Math.round(ci * (cw + gap)), w: Math.round(cw) };
  });
  // Cards sit beside where their sessions came from, so strands run as
  // straight as they can; heights by sessions, none too short to read.
  columns.forEach((col, ci) => {
    if (ci > 0 && !col.def.fixed) {
      const prev = columns[ci - 1];
      const at = new Map();
      for (const s of sessions) {
        const a = keyAt[ci - 1](s);
        const b = keyAt[ci](s);
        if (a == null || b == null) continue;
        const p = prev.byKey.get(a);
        const e = at.get(b) || { w: 0, y: 0 };
        e.w += 1;
        e.y += p.y + p.h / 2;
        at.set(b, e);
      }
      const bary = (k) => (at.get(k)?.w ? at.get(k).y / at.get(k).w : 1e9);
      col.cards.sort((a, b) => (a.key === "__more") - (b.key === "__more") || bary(a.key) - bary(b.key));
    }
    const avail = height - CARD_GAP * (col.cards.length - 1);
    // An outcome that needs a look is never squeezed to a sliver: it keeps
    // room to say where its sessions came from. Its count stays the truth.
    const floorOf = (c) => (col.def.fixed ? { met: 150, not_verified: 96, not_met: 72, set_aside: 64 }[c.key] || CARD_MIN : CARD_MIN);
    // Review gives its room to what needs a person: the flagged outcomes
    // share the column by how many sessions each holds, with room to say
    // where they came from and who ran them; what nothing was flagged in is
    // a short card, its size said by the strand into it.
    const flagged = col.def.fixed ? col.cards.reduce((a, c) => a + (c.key === "none" ? 0 : c.count), 0) : 0;
    const quiet = flagged && col.cards.some((c) => c.key === "none") ? QUIET_H : 0;
    let hs = flagged
      ? col.cards.map((c) => (c.key === "none" ? QUIET_H : Math.max(floorOf(c), (c.count / flagged) * (avail - quiet))))
      : col.cards.map((c) => Math.max(floorOf(c), (c.count / total) * avail));
    const over = hs.reduce((a, h) => a + h, 0) - avail;
    if (over > 0) {
      const flex = hs.map((h, i) => h - floorOf(col.cards[i]));
      const sum = flex.reduce((a, b) => a + b, 0) || 1;
      hs = hs.map((h, i) => h - (over * flex[i]) / sum);
    }
    let y = 0;
    col.cards.forEach((c, i) => {
      c.x = col.x;
      c.w = col.w;
      c.y = y;
      c.h = Math.round(hs[i]);
      y += c.h + CARD_GAP;
    });
  });
  // One ribbon per pair of cards in neighbouring columns, as thick at each
  // end as its sessions are of that card; ribbons leave a card in the order
  // of where they go, so they cross as little as they can.
  const strands = [];
  for (let ci = 0; ci < n - 1; ci += 1) {
    const L = columns[ci];
    const R = columns[ci + 1];
    const agg = new Map();
    for (const s of sessions) {
      const a = keyAt[ci](s);
      const b = keyAt[ci + 1](s);
      if (a == null || b == null) continue;
      const k = `${ci}\u0000${a}\u0000${b}`;
      const e = agg.get(k) || { key: k, ci, a, b, count: 0, cls: { met: 0, flagged: 0, clear: 0 } };
      e.count += 1;
      e.cls[CLASS_OF[s.outcome || "none"]] += 1;
      agg.set(k, e);
    }
    const list = [...agg.values()];
    for (const card of L.cards) {
      const unit = card.h / Math.max(1, card.count);
      let off = card.y;
      for (const st of list.filter((t) => t.a === card.key).sort((x, y) => R.byKey.get(x.b).y - R.byKey.get(y.b).y)) {
        st.y0 = off;
        st.t0 = st.count * unit;
        off += st.t0;
      }
    }
    for (const card of R.cards) {
      const unit = card.h / Math.max(1, card.count);
      let off = card.y;
      for (const st of list.filter((t) => t.b === card.key).sort((x, y) => L.byKey.get(x.a).y - L.byKey.get(y.a).y)) {
        st.y1 = off;
        st.t1 = st.count * unit;
        off += st.t1;
      }
    }
    for (const st of list) {
      st.x0 = L.x + L.w;
      st.x1 = R.x;
    }
    strands.push(...list);
  }
  return { columns, strands, keyAt, total };
}

// A ribbon plugs straight into each card before it bends, so it reads as
// leaving one card and arriving at the other, not as a thread between them.
const stubOf = (x0, x1) => Math.min(14, (x1 - x0) * 0.12);
const ribbon = (x0, y0, t0, x1, y1, t1) => {
  const stub = stubOf(x0, x1);
  const a = x0 + stub;
  const b = x1 - stub;
  const xm = (a + b) / 2;
  return `M${x0},${y0}L${a},${y0}C${xm},${y0} ${xm},${y1} ${b},${y1}L${x1},${y1}L${x1},${y1 + t1}L${b},${y1 + t1}C${xm},${y1 + t1} ${xm},${y0 + t0} ${a},${y0 + t0}L${x0},${y0 + t0}Z`;
};
// A band of a ribbon: from `from` to `to` of the way down its thickness.
const band = (st, from, to, k = 1) => ribbon(st.x0, st.y0 + st.t0 * k * from, st.t0 * k * (to - from), st.x1, st.y1 + st.t1 * k * from, st.t1 * k * (to - from));

// The strands as review left them: the part that was flagged amber and the
// part that met a standard red along each one's top edge, softly at rest, the
// amber softest, so a standard met is what stands out (the rest of it is the
// board's grey). Each colour is one shape, so strands lying
// over each other are one surface, with no seam where they meet. `sub` draws
// a pointed card's flow (its share of each strand, by strand key) a shade up,
// in full colour, the rest standing back behind it (`quiet`).
function Strands({ strands, sub = null, quiet = false }) {
  const { base, flagged, met } = useMemo(() => {
    let b = "";
    let f = "";
    let m = "";
    for (const st of strands) {
      const part = sub ? sub.get(st.key) : st;
      if (!part) continue;
      const k = part.count / st.count;
      const mm = part.cls.met / part.count;
      const ff = part.cls.flagged / part.count;
      b += band(st, 0, 1, k);
      if (ff) f += band(st, mm, mm + ff, k);
      if (mm) m += band(st, 0, mm, k);
    }
    return { base: b, flagged: f, met: m };
  }, [strands, sub]);
  return (
    <g opacity={quiet ? 0.18 : 1}>
      {sub ? <path d={base} style={{ fill: LIT_FILL }} /> : null}
      {flagged ? <path d={flagged} fill={FLAGGED} fillOpacity={sub ? 0.6 : 0.18} /> : null}
      {met ? <path d={met} fill={MET} fillOpacity={sub ? 0.8 : 0.45} /> : null}
    </g>
  );
}

// The board as one shape: its cards and the strands between them, what is
// not review's colour (a grey card, the unflagged part of a strand) filled
// one grey. One fill for the lot, so where a strand meets its card, or
// strands lie against each other, there is no seam.
function Body({ geo, fill = REST_FILL }) {
  const { body, open } = useMemo(() => {
    let all = "";
    let cards = "";
    for (const col of geo.columns) {
      for (const c of col.cards) {
        const r = `M${c.x},${c.y}h${c.w}v${c.h}h${-c.w}Z`;
        all += r;
        if (!poolOf(col, c.key)) cards += r;
      }
    }
    let clear = "";
    let whole = "";
    for (const st of geo.strands) {
      const flagged = (st.cls.met + st.cls.flagged) / st.count;
      whole += band(st, 0, 1);
      if (flagged < 1) clear += band(st, flagged, 1);
    }
    return { body: all + whole, open: cards + clear };
  }, [geo]);
  return (
    <>
      <path d={body} style={{ fill: "var(--workspace-bg)" }} />
      <path d={open} style={{ fill }} />
    </>
  );
}

// How a card's sessions went over the range: a bar per stretch, in the
// card's own colour.
export const Trend = memo(function Trend({ series, w, h = 14, color = "var(--muted)" }) {
  const max = Math.max(1, ...series);
  const bw = w / series.length;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="block w-full" style={{ height: h }} aria-hidden="true">
      {series.map((v, i) => (v ? <rect key={i} x={i * bw + 0.5} y={h - Math.max(1, (v / max) * h)} width={Math.max(0.5, bw - 1)} height={Math.max(1, (v / max) * h)} fill={color} opacity="0.45" /> : null))}
    </svg>
  );
});

// What a card is made of, a list to a column: drawn once per card, not again
// as the board is pointed at.
const CardLists = memo(function CardLists({ lists }) {
  return (
    <span className="mt-[8px] grid gap-x-[16px]" style={{ gridTemplateColumns: `repeat(${Math.max(1, lists.length)}, minmax(0, 1fr))` }} data-flow-card-lists="">
      {lists.map((list, i) => (
        <span key={list.label || i} className="grid min-w-0 content-start">
          {list.label ? <span className={`${EYEBROW_CLASS} leading-[16px]`}>{list.label}</span> : null}
          {list.rows.map(([label, n]) => (
            <span key={label} className="flex min-w-0 justify-between gap-[8px] text-[11px] leading-[15px]">
              <span className="min-w-0 truncate text-[color:var(--muted)]">{label}</span>
              <span className="shrink-0 tabular-nums text-[color:var(--muted-soft)]">{NUM.format(n)}</span>
            </span>
          ))}
        </span>
      ))}
    </span>
  );
});
// A card's lists: no more than two side by side, the first few rows of each.
const LIST_COLUMNS = 2;
const LIST_ROWS = 5;

// What a card `h` tall has room for, line by line: its name; its count,
// share and flags; for an outcome, what it means. A line that would not fit
// is left out, never cut. A card with room says its count large, under its
// name. `said` is how tall what it says is.
export function cardLines(h, { more = false, review = false, none = false } = {}) {
  const big = !more && h >= 84;
  let room = h - 12 - 18 - (big ? 30 : 0);
  const statsLine = room >= 15;
  if (statsLine) room -= 15;
  const whyLine = review && !none && room >= 15;
  return { big, statsLine, whyLine, said: 18 + (big ? 30 : 0) + (statsLine ? 15 : 0) + (whyLine ? 15 : 0) };
}

// `drill`: the clause a click narrows the search to (none for the folded
// rest); `held`, whether the search is narrowed to it already, when a click
// widens it back out. `inAll`: how many of all the search reaches a review
// card stands for, where the board is drawn from the newest of them, said
// at rest and on the card pointed at.
const Card = memo(function Card({ col, card, total, lit, hot, hue, asMachine = false, drill = null, held = false, inAll = null, onPoint, onLeave, onDrill }) {
  const def = col.def;
  const more = card.key === "__more";
  const review = !!def.fixed;
  const shown = lit ? lit.get(`${col.ci}\u0000${card.key}`) || 0 : null;
  const faded = lit && !shown;
  const name = more ? `${NUM.format(card.folded.length)} more ${def.noun}` : def.name(card.key);
  const tone = review ? (card.key === "met" ? MET : card.key === "none" ? null : FLAGGED) : null;
  const { big, statsLine, whyLine, said } = cardLines(card.h, { more, review, none: card.key === "none" });
  // Under what it says, what it is made of, while there is room for two rows
  // or more of it: for an outcome, where its sessions came from and who ran
  // them; for the rest, their lists of more than one part; for the folded
  // card, what it folds.
  const listRows = Math.min(LIST_ROWS, Math.max(0, Math.floor((card.h - 12 - said - 8 - 16) / 15)));
  const listCols = more ? 1 : Math.max(1, Math.min(LIST_COLUMNS, Math.floor((card.w - 18 + 16) / 196)));
  const lists = useMemo(() => {
    if (listRows < 2) return [];
    if (more) return [{ label: null, rows: card.folded.slice(0, listRows).map(([v, n]) => [def.name(v), n]) }];
    const parts = [];
    for (const p of def.lists || []) {
      if (parts.length >= listCols) break;
      const rows = tally(card.sessions, p.of);
      if (rows.length > (review ? 0 : 1)) parts.push({ label: p.label, rows: rows.slice(0, listRows).map(([v, n]) => [p.name(v), n]) });
    }
    return parts;
  }, [listRows, more, card, def, listCols, review]);
  const count = lit ? shown : card.count;
  const pool = poolOf(col, card.key);
  const filled = !!lit && !!shown;
  return (
    <button
      type="button"
      onMouseEnter={() => onPoint(col.ci, card.key)}
      onMouseLeave={onLeave}
      onClick={drill ? () => onDrill(drill) : undefined}
      aria-pressed={drill ? held : undefined}
      className={`absolute flex ${drill ? "cursor-pointer" : "cursor-default"} flex-col overflow-hidden border border-transparent px-[9px] pb-[6px] pt-[6px] text-left transition-[opacity,background-color] duration-150`}
      style={{
        left: card.x, top: card.y, width: card.w, height: card.h,
        opacity: faded ? 0.32 : 1,
        // In a pointed card's flow, a card fills a shade up; the card pointed
        // at, a shade more. At rest the board's own fill shows through.
        background: pool ? (hot || filled ? POOL[pool].hot : POOL[pool].fill) : hot ? HOT_FILL : filled ? LIT_FILL : "transparent",
      }}
      data-flow-card={`${col.ci}:${card.key}`}
    >
      {big ? (
        <>
          <span className="flex min-w-0 items-center gap-[6px] leading-[18px]">
            {hue ? <span className="h-[8px] w-[8px] shrink-0 rounded-[1px]" style={{ background: hue }} /> : null}
            <span className="min-w-0 truncate text-[12px] text-[color:var(--ink)]">
              {asMachine ? <span className="text-[color:var(--muted)]">{"machine "}</span> : null}
              {name}
            </span>
          </span>
          <span className="mt-[2px] text-[22px] font-medium leading-[28px] tabular-nums" style={{ color: tone || "var(--ink)" }} data-flow-card-count="">{NUM.format(count)}</span>
          <span className="flex min-w-0 items-baseline gap-[6px] overflow-hidden whitespace-nowrap text-[11px] leading-[15px] tabular-nums">
            {lit ? (
              <>
                <span className="text-[color:var(--muted)]">{`of ${NUM.format(card.count)}`}</span>
                {hot && review && inAll != null && inAll !== card.count ? <span className="text-[color:var(--muted)]" data-flow-card-in-all="">{`${NUM.format(inAll)} in all`}</span> : null}
              </>
            ) : (
              <>
                <span className="text-[color:var(--muted-soft)]">{`${pctOf(card.count, total)} of sessions`}</span>
                {review && inAll != null && inAll !== card.count ? <span className="text-[color:var(--muted)]" data-flow-card-in-all="">{`${NUM.format(inAll)} in all`}</span> : null}
                {!review && card.cls.met ? <span style={{ color: MET }}>{`${NUM.format(card.cls.met)} met`}</span> : null}
                {!review && card.cls.flagged ? <span style={{ color: FLAGGED }}>{`${NUM.format(card.cls.flagged)} flagged`}</span> : null}
              </>
            )}
          </span>
          {whyLine ? <span className="truncate text-[11px] leading-[15px] text-[color:var(--muted-soft)]">{OUTCOME_WHY[card.key]}</span> : null}
          {lists.length ? <CardLists lists={lists} /> : null}
        </>
      ) : (
      <>
      <span className="flex min-w-0 items-center gap-[6px] leading-[18px]">
        {hue ? <span className="h-[8px] w-[8px] shrink-0 rounded-[1px]" style={{ background: hue }} /> : null}
        {review ? <span className="shrink-0 text-[12px] font-medium tabular-nums" style={{ color: tone || "var(--ink)" }}>{NUM.format(count)}</span> : null}
        <span className={`min-w-0 truncate text-[12px] ${more ? "text-[color:var(--muted)]" : "text-[color:var(--ink)]"}`}>
          {/* A machine no person is known for, said as one, as a search pill says it. */}
          {asMachine ? <span className="text-[color:var(--muted)]">{"machine "}</span> : null}
          {name}
        </span>
        {!statsLine && !review ? <span className="ml-auto shrink-0 text-[11px] tabular-nums text-[color:var(--muted)]">{NUM.format(count)}</span> : null}
      </span>
      {statsLine ? (
        <span className="flex min-w-0 items-baseline gap-[6px] overflow-hidden whitespace-nowrap text-[11px] leading-[15px] tabular-nums">
          {review ? (
            <>
              <span className="text-[color:var(--muted-soft)]">{lit ? `of ${NUM.format(card.count)}` : `${pctOf(card.count, total)} of sessions`}</span>
              {(!lit || hot) && inAll != null && inAll !== card.count ? <span className="text-[color:var(--muted)]" data-flow-card-in-all="">{`${NUM.format(inAll)} in all`}</span> : null}
            </>
          ) : (
            <>
              <span className={lit ? "text-[color:var(--ink)]" : "text-[color:var(--muted)]"}>{lit ? `${NUM.format(count)} of ${NUM.format(card.count)}` : NUM.format(card.count)}</span>
              {!lit && card.w >= 190 ? <span className="text-[color:var(--muted-soft)]">{pctOf(card.count, total)}</span> : null}
              {card.cls.met ? <span style={{ color: MET }}>{`${NUM.format(card.cls.met)} met`}</span> : null}
              {card.cls.flagged ? <span style={{ color: FLAGGED }}>{`${NUM.format(card.cls.flagged)} flagged`}</span> : null}
            </>
          )}
        </span>
      ) : null}
      {whyLine ? <span className="truncate text-[11px] leading-[15px] text-[color:var(--muted-soft)]">{OUTCOME_WHY[card.key]}</span> : null}
      {lists.length ? <CardLists lists={lists} /> : null}
      </>
      )}
    </button>
  );
});

// What the search box finds, a section each in this order: a step of the
// board where there is one, so the board can light the card it names.
export const FINDS = [
  { label: "Groups", one: "Group", field: "group", def: STEPS.group },
  { label: "People", one: "Person", field: "person", def: STEPS.person },
  { label: "Agents", one: "Agent", field: "agent", def: STEPS.agent },
  { label: "Work", one: "Work", field: "topic", def: STEPS.topic },
  { label: "MCP", one: "MCP", field: "mcp", of: (s) => s.mcp, clause: (v) => `mcp:${quoteValueIfNeeded(v)}` },
  { label: "Skills", one: "Skill", field: "skill", of: (s) => s.skills, clause: (v) => `skill:${quoteValueIfNeeded(v)}` },
  { label: "Tools", one: "Tool", field: "tool", of: (s) => s.tools, clause: (v) => `tool:${quoteValueIfNeeded(v)}` },
  { label: "Machines", one: "Machine", field: "machine", def: STEPS.machine },
  { label: "Models", one: "Model", field: "model", of: (s) => s.model, clause: (v) => `model:${quoteValueIfNeeded(v)}` },
].map((k) => (k.def ? { ...k, of: k.def.of, name: k.def.name, clause: k.def.clause } : { name: (v) => v, ...k }));
export const FINDS_NO_GROUPS = FINDS.filter((k) => k.def !== STEPS.group);
const CAPTIONS = 26;
// The line under the flow saying which sessions it is drawn from.
export const SAMPLE_H = 22;

// What the board shows while it reads its sessions: its columns' cards as
// empty outlines, even, nothing named in them and no strands between them
// (their shape is the reading's to say), as tall as the board.
const SKELETON_CARDS = [4, 6, 5];
function skeletonOf(steps, width, height) {
  const n = steps.length;
  const gap = n > 1 ? Math.max(56, Math.min(150, width * 0.09)) : 0;
  const cw = Math.max(140, (width - gap * (n - 1)) / n);
  return {
    strands: [],
    columns: steps.map((def, ci) => {
      const k = SKELETON_CARDS[ci % SKELETON_CARDS.length];
      const h = Math.max(CARD_MIN, Math.floor((height - CARD_GAP * (k - 1)) / k));
      const x = Math.round(ci * (cw + gap));
      const w = Math.round(cw);
      return { ci, def, x, w, cards: Array.from({ length: k }, (_, i) => ({ key: `s${i}`, x, w, y: i * (h + CARD_GAP), h })) };
    }),
  };
}

export const Bar = ({ w, h = 10, dim = 0.7 }) => <span className="block rounded-[1px] bg-[color:var(--line-soft)]" style={{ width: w, height: h, opacity: dim }} aria-hidden="true" />;

// `onPick(clause)`: a card clicked, to narrow the search to it (or widen it
// back out).
// `reached`: drawn from the newest sessions, how many of all the search
// reaches review left at each place ({ met, not_verified, ..., none }).
// Each agent's colour, given once and kept as the search narrows, so an
// agent's card and its part of the page's Agents section keep the colour
// they had: the sessions' busiest agents first.
export function useAgentHues(facts) {
  const huesRef = useRef(new Map());
  return useMemo(() => {
    const kept = huesRef.current;
    const ids = facts?.rings ? facts.rings.agents.map((a) => a.tag) : tally(facts?.sessions || [], (x) => x.agent).map(([id]) => id);
    for (const id of ids) if (id && !kept.has(id)) kept.set(id, AGENT_HUES[kept.size % AGENT_HUES.length]);
    return new Map(kept);
  }, [facts?.rings, facts?.sessions]);
}

// A column that can show one step or another (the groups, or the people in
// them): its steps named side by side in its caption, the one shown as a
// caption is, the other faint until pointed at. `onSwap(ci, def)` hears it
// picked.
function StepSwitch({ col, options, onSwap }) {
  return (
    <span className="flex items-baseline gap-[10px]" role="group" aria-label={options.map((def) => def.label).join(" or ")} data-flow-switch={col.ci}>
      {options.map((def) => {
        const on = def === col.def;
        return (
          <button
            key={def.label}
            type="button"
            aria-pressed={on}
            onClick={on ? undefined : () => onSwap(col.ci, def)}
            className={`${EYEBROW_CLASS} transition-opacity ${on ? "cursor-default" : "opacity-[0.45] hover:opacity-100"}`}
            data-flow-switch-option={def.label}
          >
            {def.label}
          </button>
        );
      })}
    </span>
  );
}

// The clause that narrows the search to a step's value. Without a
// directory's reading of each session, a person is the machines they are
// on: the clause that narrows to them names those machines.
export function useClauseFor(facts) {
  const machinesOf = useMemo(() => {
    if (!facts.partial) return null;
    const by = new Map();
    for (const s of facts.sessions) {
      if (!s.person || !s.machine || !s.machineName) continue;
      if (!by.has(s.person)) by.set(s.person, new Set());
      by.get(s.person).add(s.machineName);
    }
    return by;
  }, [facts]);
  return useCallback((def, v) => {
    if (def !== STEPS.person || !machinesOf) return def.clause(v);
    const ms = [...(machinesOf.get(v) || [])].map((m) => `machine:${quoteValueIfNeeded(m)}`);
    return ms.length > 1 ? `(${ms.join(" OR ")})` : ms[0] || null;
  }, [machinesOf]);
}

// The counts over all the search reaches (`metrics`), once they are the
// search's own: while the next search's are read, the last one's are not
// said beside what the next one draws.
export const countsOf = (metrics) => (metrics?.loading || metrics?.stale ? null : metrics?.data ?? null);

// The line under a welcome view saying which sessions it is drawn from:
// every one the search reaches, or past the match's cap the most recent, and
// when they were last active; `aside` at its end.
export function SampleLine({ facts, top, width, aside = null }) {
  const sample = useMemo(() => {
    // Counted over all the search reaches (lib/ringCounts.js): all of them,
    // and when they were active.
    if (facts.rings) return facts.rings.total ? { n: facts.rings.total, capped: false, from: facts.firstMs ?? null, to: facts.lastMs ?? null } : null;
    const list = facts.sessions;
    if (!list.length) return null;
    let from = Infinity;
    let to = -Infinity;
    for (const x of list) {
      const t = activeAt(x);
      if (!Number.isFinite(t)) continue;
      if (t < from) from = t;
      if (t > to) to = t;
    }
    return { n: list.length, capped: !!facts.capped, from: Number.isFinite(from) ? from : null, to: Number.isFinite(to) ? to : null };
  }, [facts.rings, facts.firstMs, facts.lastMs, facts.sessions, facts.capped]);
  // While the search's sessions are read (a narrowing click drawn meanwhile
  // from those already in), the line says so, a dot beating beside it.
  const reading = facts.narrowing ? "narrowing" : facts.stale ? "reading" : null;
  if (!sample && !aside && !reading) return null;
  return (
    <div className="absolute left-0 flex items-baseline gap-[16px]" style={{ top, width }}>
      <p className="m-0 min-w-0 flex-1 truncate text-[11px] leading-[16px] text-[color:var(--muted-soft)]" data-flow-sample={sample ? "" : undefined} data-flow-reading={reading || undefined}>
        {reading ? <span className="mr-[6px] inline-block h-[6px] w-[6px] rounded-full bg-[color:var(--muted)] align-middle motion-safe:animate-pulse" aria-hidden="true" /> : null}
        {reading === "narrowing" ? "Showing what was already read; reading the rest of the search"
          : reading ? "Reading the sessions the search reaches"
            : sample ? (
              <>
                {sample.capped ? `Drawn from the ${NUM.format(sample.n)} most recent sessions` : `Drawn from all ${NUM.format(sample.n)} sessions in reach`}
                {sample.from != null ? `, last active ${whenText(sample.from)} to ${whenText(sample.to)}` : ""}
              </>
            ) : null}
      </p>
      {aside}
    </div>
  );
}

// `hues`: the agents' colours, as the page gives them (useAgentHues); the
// board keeps its own without. `swaps`: beside `steps`, for a column that can
// show another step, the steps it can show (the one in `steps` among them);
// `onSwap(ci, def)` hears one picked. `groups`: whether there are groups to
// find (none where the directory gives no one a department). `aside`: drawn
// at the end of the line under the board.
function FlowBoard({ facts, steps, width, height, reached = null, onPick, hues: given = null, swaps = null, onSwap = null, groups = true, aside = null }) {
  // The board: its column captions, then its cards.
  const flowTop = CAPTIONS;
  // The flow takes the board's height but for the line under it.
  const flowH = Math.max(240, height - flowTop - SAMPLE_H);
  // Until review is read (facts.reviewed false) its column holds no cards
  // and no strand runs into it: the rest of the board is drawn as it will
  // stay, the column holding a placeholder.
  const unreviewed = facts.reviewed === false;
  const drawn = useMemo(() => (unreviewed ? steps.map((d) => (d.fixed ? { ...d, of: () => null } : d)) : steps), [steps, unreviewed]);
  const geo = useMemo(() => layout(facts.sessions, drawn, { width, height: flowH }), [facts.sessions, drawn, width, flowH]);
  const pending = useMemo(() => {
    if (!unreviewed) return null;
    const col = skeletonOf(steps, width, flowH).columns.find((c) => c.def.fixed);
    return col ? { columns: [col], strands: [] } : null;
  }, [unreviewed, steps, width, flowH]);
  const reading = facts.status === "loading" && !facts.sessions.length;
  const skeleton = useMemo(() => (reading ? skeletonOf(steps, width, flowH) : null), [reading, steps, width, flowH]);
  const clauseFor = useClauseFor(facts);
  const [query] = useWorkspaceSearch();
  // What the sidebar's search finds among: what this board read.
  useLendFinder({ kinds: groups ? FINDS : FINDS_NO_GROUPS, sessions: facts.sessions, clauseFor });
  // The people column's cards that are machines no person is known for.
  const machineKeys = useMemo(() => new Set(facts.sessions.filter((x) => x.personIsMachine).map((x) => x.person)), [facts.sessions]);
  const [pointed, setPoint] = useState(null);
  const onPoint = useCallback((ci, key) => setPoint({ ci, key }), []);
  const onLeave = useCallback(() => setPoint(null), []);
  // With nothing pointed at, the board stands as if its "Standard met" card
  // were: where what met a standard came from and who ran it, the rest
  // faded back, so the board says first what it is there for. With nothing
  // met in reach, the first other place review flagged; with nothing
  // flagged, none.
  const restPoint = useMemo(() => {
    const ci = geo ? geo.columns.findIndex((c) => c.def.fixed) : -1;
    if (ci < 0) return null;
    const keys = new Set(geo.columns[ci].cards.map((c) => c.key));
    const key = OUTCOMES.map(([id]) => id).find((id) => id !== "none" && keys.has(id));
    return key ? { ci, key } : null;
  }, [geo]);
  const point = pointed || restPoint;
  const own = useAgentHues(facts);
  const hues = given || own;
  // The pointed card's sessions: how many of each card and of each ribbon
  // they are, by where review left them.
  const lit = useMemo(() => {
    if (!geo || !point) return null;
    const hit = (x) => geo.keyAt[point.ci](x) === point.key;
    const cards = new Map();
    const ribbons = new Map();
    for (const x of facts.sessions) {
      if (!hit(x)) continue;
      const c = CLASS_OF[x.outcome || "none"];
      for (let ci = 0; ci < drawn.length; ci += 1) {
        const k = geo.keyAt[ci](x);
        if (k == null) continue;
        cards.set(`${ci}\u0000${k}`, (cards.get(`${ci}\u0000${k}`) || 0) + 1);
        if (ci < drawn.length - 1) {
          const b = geo.keyAt[ci + 1](x);
          if (b == null) continue;
          const key = `${ci}\u0000${k}\u0000${b}`;
          const e = ribbons.get(key) || { count: 0, cls: { met: 0, flagged: 0, clear: 0 } };
          e.count += 1;
          e.cls[c] += 1;
          ribbons.set(key, e);
        }
      }
    }
    return { cards, ribbons };
  }, [geo, point, facts.sessions, drawn]);
  const shown = geo || skeleton;
  return (
    <div className="relative" style={{ width, height }} data-flow-board="">
      <SampleLine facts={facts} top={flowTop + flowH + 6} width={width} aside={aside} />
      {shown ? shown.columns.map((col) => (
        <span key={col.ci} className="absolute flex items-baseline justify-between gap-[8px]" style={{ left: col.x, top: 0, width: col.w }}>
          {swaps?.[col.ci] && onSwap ? <StepSwitch col={col} options={swaps[col.ci]} onSwap={onSwap} /> : <span className={EYEBROW_CLASS}>{col.def.label}</span>}
          {geo && col.def.fixed && unreviewed ? (
            <span className="inline-flex items-center gap-[6px] text-[11px] text-[color:var(--muted)]" aria-live="polite" data-flow-review-reading="">
              {facts.reviewFailed ? "The review could not be read" : (
                <>
                  <span className="h-[6px] w-[6px] animate-pulse rounded-full bg-[color:var(--green)]" aria-hidden="true" />
                  Reading the review
                </>
              )}
            </span>
          ) : geo ? <span className="text-[11px] tabular-nums text-[color:var(--muted-soft)]">{col.def.fixed ? "" : NUM.format(col.cards.reduce((a, c) => a + (c.folded ? c.folded.length : 1), 0))}</span> : null}
        </span>
      )) : null}
      {geo ? (
        // A board read before, while the next reading is under way, stays
        // in place, dimmed, rather than going blank.
        <div className="absolute left-0 transition-opacity duration-200" style={{ top: flowTop, opacity: facts.stale ? 0.45 : 1 }}>
          <svg className="absolute left-0 top-0 overflow-visible" width={width} height={flowH} aria-hidden="true">
            <Body geo={geo} />
            {pending ? <Body geo={pending} /> : null}
            <Strands strands={geo.strands} quiet={!!lit} />
            {lit ? <Strands strands={geo.strands} sub={lit.ribbons} /> : null}
          </svg>
          {geo.columns.flatMap((col) => col.cards.map((card) => {
            // The folded rest stands for many values: no one clause narrows to it.
            const drill = card.key === "__more" ? null : clauseFor(col.def, card.key);
            return (
              <Card
                key={`${col.ci}:${card.key}`}
                col={col}
                card={card}
                total={geo.total}
                lit={lit?.cards || null}
                hot={!!point && point.ci === col.ci && point.key === card.key}
                hue={col.def === STEPS.agent ? hues.get(card.key) : null}
                asMachine={col.def === STEPS.person && machineKeys.has(card.key)}
                drill={drill}
                held={!!drill && holdsClause(query, drill)}
                inAll={col.def.fixed && reached ? reached[card.key] ?? null : null}
                onPoint={onPoint}
                onLeave={onLeave}
                onDrill={onPick}
              />
            );
          }))}
          {pending ? pending.columns[0].cards.map((card, i) => (
            <span key={`pending:${card.key}`} className="absolute grid content-start gap-[6px] px-[9px] pt-[9px]" style={{ left: card.x, top: card.y, width: card.w, height: card.h }} aria-hidden="true" data-flow-review-pending="">
              {card.h >= 30 ? <Bar w={`${34 + ((i * 23) % 30)}%`} /> : null}
              {card.h >= 52 ? <Bar w={`${18 + ((i * 13) % 16)}%`} h={8} dim={0.4} /> : null}
            </span>
          )) : null}
        </div>
      ) : skeleton ? (
        <div className="absolute left-0" style={{ top: flowTop }} aria-busy="true" data-flow-skeleton="">
          <svg className="absolute left-0 top-0 overflow-visible" width={width} height={flowH} aria-hidden="true">
            <Body geo={skeleton} />
          </svg>
          {skeleton.columns.flatMap((col) => col.cards.map((card, i) => (
            <span key={`${col.ci}:${card.key}`} className="absolute grid content-start gap-[6px] px-[9px] pt-[9px]" style={{ left: card.x, top: card.y, width: card.w, height: card.h }}>
              {card.h >= 30 ? <Bar w={`${34 + ((i * 23 + col.ci * 11) % 30)}%`} /> : null}
              {card.h >= 52 ? <Bar w={`${18 + ((i * 13 + col.ci * 7) % 16)}%`} h={8} dim={0.4} /> : null}
            </span>
          )))}
        </div>
      ) : (
        <p className="absolute m-0 text-[12px] text-[color:var(--muted)]" style={{ top: flowTop }}>
          {facts.status === "error" ? "The sessions could not be read." : "No session matches this search."}
        </p>
      )}
    </div>
  );
}

// Drawn again only when what it is handed changes, not each time the page
// around it is (its own room changing as a column's seam is dragged).
export default memo(FlowBoard);
